/**
 * FB-1430 — megamenu disclosure semantics.
 *
 * Findings 81085 (custom-navigation), 81886 (missing expanded state) and
 * 81089 (caret reads as a separate control).
 *
 * The header megamenu reveals its panel on hover and on focus. Production runs
 * `select_motion: hover`, and tabbing to a top-level item already expands its
 * panel — so the menu is operable by keyboard. What was missing is that
 * nothing announced the state: no `aria-expanded` anywhere, and the caret sat
 * outside the link as an undescribed sibling.
 *
 * Why this is JS and not markup: the reveal is driven by an `is-focus` class
 * that `snippets/header-megamenu-block.liquid` toggles from 32 separate
 * inline Alpine handlers across 8 menu-type branches. Mirroring that class
 * onto `aria-expanded` once, here, covers every branch and any future one,
 * instead of editing 32 call sites and hoping they stay in sync.
 *
 * Deliberately NOT done: turning the caret into its own <button>. That would
 * add a second tab stop to every top-level nav item — which is what finding
 * 81889 already complains about — and it is unnecessary, because focusing the
 * link is what opens the panel. The caret is marked decorative instead.
 *
 * Also implements WCAG 2.2 SC 1.4.13 (Content on Hover or Focus): hover- or
 * focus-revealed content must be dismissible without moving focus. Escape
 * collapses the open item and leaves focus where it is. Not an audit finding —
 * caught while implementing the above.
 */
(() => {
  'use strict';

  const OPEN_CLASS = 'is-focus';

  /**
   * A megamenu parent item is an element that carries the reveal class and
   * contains a caret. Matching on structure rather than on a class name keeps
   * this working across all 8 branches in header-megamenu-block.liquid, whose
   * wrapper classes differ.
   */
  const isMenuParent = (el) =>
    el.nodeType === 1 && !!el.querySelector(':scope .x-icon-caret');

  /** The top-level link for a parent item — the first link inside it. */
  const parentLink = (el) => el.querySelector('a[href]');

  const syncExpanded = (el) => {
    const link = parentLink(el);
    if (!link) return;
    link.setAttribute('aria-expanded', el.classList.contains(OPEN_CLASS) ? 'true' : 'false');
  };

  /**
   * The caret is a purely visual affordance — it duplicates the state the
   * link's aria-expanded now conveys, and it is not focusable. Hiding it from
   * assistive tech stops it being announced as a separate, roleless control
   * (81089).
   */
  const hideCarets = (root) => {
    root.querySelectorAll('.x-icon-caret:not([aria-hidden])').forEach((caret) => {
      caret.setAttribute('aria-hidden', 'true');
    });
  };

  const initWithin = (root) => {
    hideCarets(root);
    root.querySelectorAll('.x-icon-caret').forEach((caret) => {
      // Walk up to the element that actually receives the reveal class. It is
      // the nearest ancestor that also contains the top-level link.
      let node = caret.parentElement;
      while (node && node !== root) {
        if (isMenuParent(node) && parentLink(node)) {
          syncExpanded(node);
          break;
        }
        node = node.parentElement;
      }
    });
  };

  const start = () => {
    const headers = document.querySelectorAll('header, #sticky-header-content');
    if (!headers.length) return;

    headers.forEach((header) => {
      initWithin(header);

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.target.nodeType === 1) {
            if (isMenuParent(mutation.target)) syncExpanded(mutation.target);
          } else if (mutation.type === 'childList' && mutation.addedNodes.length) {
            // Panels render from <template x-if>, so items appear after init.
            initWithin(header);
          }
        }
      });

      observer.observe(header, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class']
      });
    });

    /**
     * Escape dismisses an open megamenu (finding 81085; also WCAG 2.2 SC
     * 1.4.13, Content on Hover or Focus).
     *
     * Panel visibility is NOT the `is-focus` class — that only drives caret
     * rotation and active-link styling. The panel is a `.toggle-menu` shown by
     * removing `toggle-menu-hidden`, managed by `xHeaderMenu.selectItem()` in
     * theme.js. So this delegates to the theme's own dismissal path,
     * `xHeaderMenu.hideMenu()`, which hides every panel, clears `.clicked` and
     * updates the backdrop overlay. It ignores its argument unless isSub.
     *
     * Focus is never moved, per 1.4.13.
     *
     * Kept deliberately minimal. An earlier attempt also tried to hold the menu
     * shut against the theme's inline mouseover handlers, using a flag plus a
     * MutationObserver guard. That flagged every dropdown rather than the open
     * one, so the observer force-hid all panels permanently and the menu could
     * not be tabbed into at all. The suppression is not worth that risk — see
     * the known gap recorded in docs/accessibility/remediation.md.
     */
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!document.querySelector('.toggle-menu:not(.toggle-menu-hidden)')) return;

      const menu = window.Alpine && window.Alpine.store && window.Alpine.store('xHeaderMenu');
      if (!menu || typeof menu.hideMenu !== 'function') return;

      menu.hideMenu(null);

      // Reset the styling state so the caret un-rotates and aria-expanded
      // reports false. One mutation per element; nothing re-adds the class.
      document.querySelectorAll(`.${OPEN_CLASS}`).forEach((el) => {
        el.classList.remove(OPEN_CLASS);
        if (isMenuParent(el)) syncExpanded(el);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
